import { useShow, useNavigation } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { Card, Descriptions, Tag, Row, Col, Collapse, Button, Image } from 'antd';

interface HotelBookingDetail {
  id: number;
  userId: number;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'FAILED';
  prebookId: string;
  liteApiBookingId: string | null;
  clientReference: string;
  hotelConfirmationCode: string | null;
  hotelId: string;
  hotelName: string;
  hotelAddress: string | null;
  hotelPhoto: string | null;
  city: string | null;
  country: string | null;
  checkin: string;
  checkout: string;
  roomName: string | null;
  boardName: string | null;
  adults: number;
  children: number;
  currency: string;
  totalAmount: number;
  refundableTag: string | null;
  cancelPolicies: unknown;
  holderFirstName: string;
  holderLastName: string;
  holderEmail: string;
  holderPhone: string | null;
  cancelledAt: string | null;
  refundAmount: number | null;
  cancellationFee: number | null;
  createdAt: string;
  updatedAt: string;
  user: { id: number; fullName: string | null; phone: string } | null;
}

const statusColors: Record<string, string> = {
  PENDING: 'orange',
  CONFIRMED: 'green',
  CANCELLED: 'default',
  FAILED: 'red',
};

const statusLabels: Record<string, string> = {
  PENDING: 'Kutilmoqda',
  CONFIRMED: 'Tasdiqlangan',
  CANCELLED: 'Bekor qilingan',
  FAILED: 'Xato',
};

const formatCurrency = (value: number | null, currency = 'UZS') => {
  if (value == null) return '-';
  return `${new Intl.NumberFormat('ru-RU').format(value)} ${currency}`;
};

const formatDate = (date: string | null) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('uz-UZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatDateTime = (date: string | null) => {
  if (!date) return '-';
  return new Date(date).toLocaleString('uz-UZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const HotelBookingShow = () => {
  const { list } = useNavigation();
  const { queryResult } = useShow<HotelBookingDetail>();
  const { data, isLoading } = queryResult;
  const booking = data?.data;

  return (
    <Show
      isLoading={isLoading}
      title="Mehmonxona broni"
      headerButtons={
        <Button onClick={() => list('admin/hotels/bookings')}>Orqaga</Button>
      }
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <Card>
            {booking?.hotelPhoto && (
              <Image
                src={booking.hotelPhoto}
                alt={booking.hotelName}
                style={{ borderRadius: 8, marginBottom: 16, objectFit: 'cover' }}
                width="100%"
                height={160}
              />
            )}
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Holat">
                <Tag color={statusColors[booking?.status || 'PENDING']}>
                  {statusLabels[booking?.status || 'PENDING']}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Mehmonxona">{booking?.hotelName}</Descriptions.Item>
              <Descriptions.Item label="Manzil">
                {booking?.hotelAddress || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Shahar">
                {[booking?.city, booking?.country].filter(Boolean).join(', ') || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Summa">
                {formatCurrency(booking?.totalAmount ?? null, booking?.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Yaratilgan">
                {formatDateTime(booking?.createdAt ?? null)}
              </Descriptions.Item>
            </Descriptions>

            {booking?.status === 'CANCELLED' && (
              <div style={{ marginTop: 16, padding: 12, background: '#fafafa', borderRadius: 8 }}>
                <div style={{ fontWeight: 500 }}>Bekor qilingan</div>
                <div style={{ fontSize: 12 }}>{formatDateTime(booking.cancelledAt)}</div>
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  Qaytarilgan: {formatCurrency(booking.refundAmount, booking.currency)}
                </div>
                <div style={{ fontSize: 12 }}>
                  Bekor qilish to'lovi: {formatCurrency(booking.cancellationFee, booking.currency)}
                </div>
              </div>
            )}
          </Card>

          <Card title="Mijoz ma'lumotlari" style={{ marginTop: 24 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Ism (buyurtma)">
                {booking?.holderFirstName} {booking?.holderLastName}
              </Descriptions.Item>
              <Descriptions.Item label="Email">{booking?.holderEmail || '-'}</Descriptions.Item>
              <Descriptions.Item label="Telefon">
                {booking?.holderPhone || booking?.user?.phone || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Ilova hisobi">
                {booking?.user
                  ? `${booking.user.fullName || 'Ism kiritilmagan'} (${booking.user.phone})`
                  : `#${booking?.userId ?? '-'}`}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card title="Bron tafsilotlari">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Kirish sanasi">
                {formatDate(booking?.checkin ?? null)}
              </Descriptions.Item>
              <Descriptions.Item label="Chiqish sanasi">
                {formatDate(booking?.checkout ?? null)}
              </Descriptions.Item>
              <Descriptions.Item label="Xona">{booking?.roomName || '-'}</Descriptions.Item>
              <Descriptions.Item label="Ovqatlanish">{booking?.boardName || '-'}</Descriptions.Item>
              <Descriptions.Item label="Mehmonlar">
                {booking?.adults} kata
                {booking?.children ? ` + ${booking.children} bola` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Bekor qilish siyosati">
                {booking?.refundableTag === 'RFN' ? (
                  <Tag color="green">Bepul bekor qilish</Tag>
                ) : booking?.refundableTag === 'NRFN' ? (
                  <Tag color="red">Qaytarilmaydi</Tag>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="liteAPI identifikatorlari" style={{ marginTop: 24 }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Prebook ID">{booking?.prebookId}</Descriptions.Item>
              <Descriptions.Item label="Booking ID (liteAPI)">
                {booking?.liteApiBookingId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Tasdiqlash kodi">
                {booking?.hotelConfirmationCode || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Client reference">
                {booking?.clientReference}
              </Descriptions.Item>
              <Descriptions.Item label="Hotel ID">{booking?.hotelId}</Descriptions.Item>
              <Descriptions.Item label="Yangilangan">
                {formatDateTime(booking?.updatedAt ?? null)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card style={{ marginTop: 24 }}>
            <Collapse
              items={[
                {
                  key: 'cancelPolicies',
                  label: 'Bekor qilish siyosati (JSON)',
                  children: (
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                      {JSON.stringify(booking?.cancelPolicies ?? null, null, 2)}
                    </pre>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </Show>
  );
};
